import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useAuthContext } from 'hooks/useAuthContext';
import { useGetWorkspaceImage } from 'hooks/workspaceImage/useGetWorkspaceImage';
import { useCreateWorkspaceImage } from 'hooks/workspaceImage/useCreateWorkspaceImage';
import { useUpdateWorkspaceImage } from 'hooks/workspaceImage/useUpdateWorkspaceImage';
import { useDeleteWorkspaceImage } from 'hooks/workspaceImage/useDeleteWorkspaceImage';
import { UserRoles } from 'constants/userRoles';
import { handleApiError } from 'helpers/handleApiError';
import { AvatarButton, AvatarLoadingButton, CancelButton, DeleteButton } from '../profile/AvatarComponent/AvatarComponent.styles';
import { LogoSection, LogoTitle, LogoControls, LogoPreview, LogoImage, EmptyLogoIcon, LogoSpinner, LogoPreviewSpinner } from './WorkspaceLogo.styles';

export const WorkspaceLogo = () => {
    const { userState } = useAuthContext();
    const { workspaceId, userRole } = userState.user.selectedWorkspace;

    if (userState.loading || !userState.isAuthenticated || userRole !== UserRoles.ADMIN || !workspaceId) {
        return null;
    }

    return <WorkspaceLogoEditor key={workspaceId} workspaceId={workspaceId} />;
};

const WorkspaceLogoEditor = ({ workspaceId }: { workspaceId: string }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const inputRef = useRef<HTMLInputElement>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string>();
    const { workspaceImage: image, workspaceImageLoading: loading, workspaceImageLoaded: loaded } = useGetWorkspaceImage(workspaceId);
    const { createWorkspaceImage, creatingWorkspaceImage } = useCreateWorkspaceImage(workspaceId);
    const { updateWorkspaceImage, updatingWorkspaceImage } = useUpdateWorkspaceImage(workspaceId);
    const { deleteWorkspaceImage, deletingWorkspaceImage: deleting } = useDeleteWorkspaceImage(workspaceId);
    const saving = creatingWorkspaceImage || updatingWorkspaceImage;

    useEffect(() => {
        const blob = selectedFile ?? image;
        setPreviewUrl(undefined);
        if (!blob) return;

        const reader = new FileReader();
        reader.onload = () => {
            if (typeof reader.result === 'string') setPreviewUrl(reader.result);
        };
        reader.onerror = () => {
            enqueueSnackbar(t('workspaceManagement.logo.previewError'), { variant: 'error' });
        };
        reader.readAsDataURL(blob);

        return () => {
            reader.onload = null;
            reader.onerror = null;
            if (reader.readyState === FileReader.LOADING) reader.abort();
        };
    }, [selectedFile, image, enqueueSnackbar, t]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;
        if (!file.type.startsWith('image/') || !file.name.includes('.') || !file.name.split('.').pop()) {
            enqueueSnackbar(t('profile.wrongFileTypeMessage'), { variant: 'error' });
            return;
        }
        setSelectedFile(file);
    };

    const handleSave = async () => {
        if (!selectedFile || saving) return;
        try {
            await (image ? updateWorkspaceImage(selectedFile) : createWorkspaceImage(selectedFile));
            setSelectedFile(null);
            enqueueSnackbar(t('workspaceManagement.logo.saveSuccess'), { variant: 'success' });
        } catch (error) {
            handleApiError(error, { t, enqueueSnackbar });
        }
    };

    const handleDelete = async () => {
        try {
            await deleteWorkspaceImage();
            enqueueSnackbar(t('workspaceManagement.logo.deleteSuccess'), { variant: 'success' });
        } catch (error) {
            handleApiError(error, { t, enqueueSnackbar });
        }
    };

    const pending = loading || saving || deleting;
    const chooseFileLabel = t(image ? 'workspaceManagement.logo.change' : 'profile.chooseFile');

    return (
        <LogoSection>
            <LogoTitle variant="h6">{t('workspaceManagement.logo.title')}</LogoTitle>
            <LogoControls>
                <LogoPreview>
                    {loading ? <LogoPreviewSpinner role="progressbar" aria-label={t('workspaceManagement.logo.title')} /> : previewUrl
                        ? <LogoImage src={previewUrl} alt={t('workspaceManagement.logo.title')} />
                        : <EmptyLogoIcon />}
                </LogoPreview>
                {selectedFile ? (
                    <>
                        <AvatarLoadingButton disableRipple loadingIndicator={<LogoSpinner role="progressbar" aria-label={t('workspaceManagement.logo.save')} />} loading={saving} disabled={pending} variant="outlined" onClick={handleSave}>{t('workspaceManagement.logo.save')}</AvatarLoadingButton>
                        <CancelButton disableRipple disabled={pending} onClick={() => setSelectedFile(null)}>{t('profile.cancelAvatar')}</CancelButton>
                    </>
                ) : (
                    <>
                        <AvatarButton disableRipple disabled={pending || !loaded} variant="outlined" onClick={() => inputRef.current?.click()}>{chooseFileLabel}</AvatarButton>
                        {image && <DeleteButton disableRipple loadingIndicator={<LogoSpinner role="progressbar" aria-label={t('workspaceManagement.logo.delete')} />} loading={deleting} disabled={pending} onClick={handleDelete}>{t('workspaceManagement.logo.delete')}</DeleteButton>}
                    </>
                )}
                <input hidden ref={inputRef} type="file" accept="image/*" onChange={handleFileChange} aria-label={chooseFileLabel} />
            </LogoControls>
        </LogoSection>
    );
};
